import { Link, NavLink } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, SparklesIcon, UserIcon } from 'lucide-react';
import { mainNav } from '../../data/navigation';
import { salon } from '../../data/content';
import { shopLabels } from '../../data/shopLabels';
import { telHref } from '../../core/rules/contact';
import { ease } from '../../core/format/motion';

/** Stagger per menu-item; 6 items = laatste start na 0,15 s. */
const ITEM_STEP_S = 0.03;
type Props = {
  id: string;
  accountLabel: string;
  onQuiz: () => void;
  onBooking: () => void;
};
const contactLink = 'inline-flex min-h-[44px] items-center text-[15px] text-ink underline decoration-ink/25 underline-offset-[5px]';

/**
 * Het mobiele menu: een eigen vlak dat het scherm onder de header vult (de pagina eronder staat
 * stil, dat regelt de header). Bovenaan de pagina's groot in de display-letter; daaronder de twee
 * vaste vervolgstappen (afspraak en huidadvies), dan account en direct contact als tekst.
 * Sluiten (Escape, route, knop) regelt de header.
 *
 * Hoogte: 100dvh min de header (aankondiging 36px + balk 72px). `dvh` volgt de adresbalk van
 * mobiele browsers, dus de knoppen onderaan vallen nooit weg.
 */
export function MobileMenu({
  id,
  accountLabel,
  onQuiz,
  onBooking
}: Props) {
  const reduce = useReducedMotion();
  const item = (i: number) => reduce ? {} : {
    initial: {
      opacity: 0,
      y: 8
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.22,
      delay: 0.04 + i * ITEM_STEP_S,
      ease
    }
  };
  return <motion.div id={id} initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.18,
    ease
  }} className="absolute inset-x-0 top-full h-[calc(100dvh-6.75rem)] overflow-y-auto overscroll-contain border-t border-line bg-shell lg:hidden" data-id="element-7890" data-mp="iz3j6fbgp426">
      <nav aria-label="Mobiele navigatie" className="flex min-h-full flex-col px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-4 sm:px-10" data-id="element-7891" data-mp="1yesvhmdcryot">
        <ul data-id="element-7892" data-mp="3bcwnki8fb2v">
          {mainNav.map((link, i) => <motion.li key={link.to} {...item(i)} className="border-b border-line" data-id="element-7893" data-mp="3a2y6u7gw0vp" data-mpi={i}>
              {/* NavLink zet zelf aria-current="page"; hier ook zichtbaar met een klei-streepje. */}
              <NavLink to={link.to} className={({
            isActive
          }) => `flex min-h-[60px] items-center justify-between font-display text-[1.75rem] font-light leading-none ${isActive ? 'text-clay' : 'text-ink'}`} data-id="element-7894" data-mp="d9k0r2rf9yng">
                {({
              isActive
            }) => <>
                    {link.label}
                    {isActive ? <span aria-hidden="true" className="h-px w-6 bg-clay" data-id="element-7895" data-mp="3qioqtqhya504" /> : <ArrowRightIcon className="h-4 w-4 text-ink/40" strokeWidth={1.5} aria-hidden="true" data-id="element-7896" data-mp="3lo2dviw804dx" />}
                  </>}
              </NavLink>
            </motion.li>)}
        </ul>

        <motion.div {...item(mainNav.length)} className="mt-8 grid grid-cols-2 gap-2" data-id="element-7897" data-mp="18ixc6yxgxp1e">
          <button type="button" onClick={onBooking} aria-haspopup="dialog" className="flex min-h-[52px] items-center justify-center rounded-full bg-ink px-4 text-[15px] text-shell transition-[background-color,transform] duration-150 ease-soft active:scale-[0.98] active:bg-clay" data-id="element-7898" data-mp="1dafdxp7bftnf">
            Afspraak maken
          </button>
          <button type="button" onClick={onQuiz} aria-haspopup="dialog" className="flex min-h-[52px] items-center justify-center gap-2 rounded-full border border-ink/20 px-4 text-[15px] text-ink transition-[border-color,transform] duration-150 ease-soft active:scale-[0.98] active:border-ink" data-id="element-7899" data-mp="1w8rlx7mhqxhf">
            <SparklesIcon className="h-4 w-4 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7900" data-mp="6s8q5wia1vhf" />
            Huidadvies
          </button>
        </motion.div>

        <motion.div {...item(mainNav.length + 1)} className="mt-auto pt-10" data-id="element-7901" data-mp="r29zjforke8b">
          <Link to="/account" className="inline-flex min-h-[44px] items-center gap-2 text-[15px] text-ink" data-id="element-7902" data-mp="r4e6y7lvfvdc">
            <UserIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7903" data-mp="hcqfi6pbeebv" />
            {accountLabel}
          </Link>
          {/* Eén behandelaar: direct contact hoort bij de hand, als rustige tekst in plaats van tegels. */}
          <div className="mt-4 border-t border-line pt-4" data-id="element-7904" data-mp="1a0cqi00vgl8r">
            <p className="text-[13px] text-muted" data-id="element-7905" data-mp="152b8lif6mryv">
              {salon.street}, {salon.city}
            </p>
            <ul className="mt-1 flex flex-wrap gap-x-6" data-id="element-7906" data-mp="110hc7mc0sa4i">
              <li data-id="element-7907" data-mp="10ywgvuhvsn5l">
                <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={contactLink} data-id="element-7908" data-mp="112kccz6vzjmy">
                  WhatsApp
                  <span className="sr-only" data-id="element-7909" data-mp="3lrhshsip4rl5">{shopLabels.viaWhatsApp}</span>
                </a>
              </li>
              <li data-id="element-7910" data-mp="3grlxgirz8ahq">
                <a href={telHref(salon.phone)} className={contactLink} data-id="element-7911" data-mp="3gr2inaxmi27x">
                  Bellen
                </a>
              </li>
              <li data-id="element-7912" data-mp="1w800dxjbnwb7">
                <a href={salon.routeUrl} target="_blank" rel="noreferrer" className={contactLink} data-id="element-7913" data-mp="218o2be939xk7">
                  Route
                  <span className="sr-only" data-id="element-7914" data-mp="25a9e0w9w05st"> naar de salon {shopLabels.newTab}</span>
                </a>
              </li>
            </ul>
          </div>
        </motion.div>
      </nav>
    </motion.div>;
}